import { ReactElement, useEffect, useMemo, useState, useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import Box from '@mui/material/Box'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Input,
  Modal,
  ModalColumnHeading,
  Select,
  UploadZone,
} from 'components'
import { useCompaniesService } from 'services'
import { CreateEditCompany } from 'utils'
import { successToast } from 'utils/toastService'
import { toast } from 'react-toastify'
import { Company } from 'types/models'
import { CrudAction, Entity, usePermissions } from '../../../../../utils/hooks'
import { FormEditableContext } from '../../../../../providers'
import { useRefreshUser } from '../../../../../store'
import { Form } from './dialogs.style'
import * as sxStyles from './dialogs.style'

interface Props {
  onConfirm?(data: any): void
  data: {
    companies: Company[]
  }
}

const useAddEditCompanyModal = ({
  data,
}: Props): [ReactElement, (args: any) => void] => {
  const queryClient = useQueryClient()
  const { checkPermission } = usePermissions()
  const { uploadCompanyFiles, createCompany, editCompany } =
    useCompaniesService()
  const refreshUser = useRefreshUser()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [company, setCompany] = useState<Company>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [lockCountdown, setLockCountdown] = useState(0)

  const toggleModal = () => setIsOpen(!isOpen)

  const createMutation = useMutation(createCompany)
  const editMutation = useMutation(editCompany)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: company?.name || '',
      icon: company?.icon || undefined,
      parent_id: !!company?.parent?.id ? String(company?.parent?.id) : null,
    },
    resolver: yupResolver(CreateEditCompany),
  })

  const parentOptions = useMemo(
    () =>
      data?.companies
        ?.filter(({ parent }) => {
          if (!company?.id) return true
          return company?.id !== parent?.id
        })
        ?.filter(({ id }) => company?.id !== id)
        ?.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    [data, company]
  )

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        icon: company.icon,
        parent_id: company.parent?.id ? String(company.parent.id) : null,
      })
    } else {
      reset({
        name: '',
        icon: undefined,
        parent_id: parentOptions?.length ? '1' : null,
      })
    }
  }, [company])

  // --- Lock logic ---
  const startLock = useCallback(() => {
    setIsLocked(true)
    setLockCountdown(5)
    const interval = setInterval(() => {
      setLockCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setIsLocked(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  const onSubmit = async (payload) => {
    if (isLocked) return

    // Prevent same-name duplicates
    const nameExists = data?.companies.some(
      (c) =>
        c.name.trim().toLowerCase() === payload.name.trim().toLowerCase() &&
        c.id !== company?.id
    )
    if (nameExists) {
      toast.error('A company with this name already exists.')
      return
    }

    const mutation = company ? editMutation : createMutation
    let icon = null

    if (payload?.icon && typeof payload?.icon !== 'string') {
      const { result } = await uploadCompanyFiles(payload.icon)
      icon = result?.file_url
    } else if (typeof payload?.icon === 'string') {
      icon = payload.icon
    }

    mutation.mutate(
      { id: company?.id, ...payload, icon },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('fetch-companies')
          refreshUser()
          toggleModal()
          successToast(`Site has been ${company ? 'updated' : 'created'}`)
          reset()
          setIsLocked(false)
        },
        onError: () => {
          setIsLocked(false)
        },
      }
    )
  }

  // Protect submission: lock immediately on click
  const handleProtectedSubmit = handleSubmit((payload) => {
    if (isLocked) return
    startLock()
    onSubmit(payload)
  })

  const onClose = () => {
    toggleModal()
    reset()
  }

  const normalizedFiles = useMemo(() => {
    const icon = watch('icon')
    if (!icon) return []
    if (typeof icon === 'string') return [icon]
    if (Array.isArray(icon)) return icon
    return [icon]
  }, [watch('icon')])

  const isReadOnly = !!company?.id
    ? !checkPermission(Entity.COMPANIES, CrudAction.UPDATE)
    : !checkPermission(Entity.COMPANIES, CrudAction.CREATE)

  const isLoading = createMutation.isLoading || editMutation.isLoading

  return [
    <FormEditableContext.Provider
      value={{
        isEditable: !isReadOnly,
      }}
    >
      <Modal
        isOpen={isOpen}
        title={company ? 'Edit Site' : 'Add Site'}
        okButtonType="submit"
        onClose={onClose}
        confirmButtonDisabled={isReadOnly || isLoading || isLocked}
        onConfirmButtonText={
          isLocked
            ? `Please wait${lockCountdown > 0 ? ` (${lockCountdown})` : ''}`
            : 'Save'
        }
      >
        <Form onSubmit={handleProtectedSubmit} id="hook-form">
          <Box sx={sxStyles.mainBox}>
            <ModalColumnHeading>Details</ModalColumnHeading>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Site Name"
                  errorMsg={errors['name']?.message}
                />
              )}
            />
            <UploadZone
              label="Upload Site Logo"
              files={normalizedFiles}
              onDrop={(uploadedFiles) => {
                setValue('icon', uploadedFiles[0])
              }}
              errorMsg={errors['icon']?.message}
              disabled={isReadOnly}
            />
          </Box>

          <Box sx={sxStyles.modalColumnHeadingBox}>
            <ModalColumnHeading>Parent Company</ModalColumnHeading>
            <Box sx={sxStyles.controllerBox}>
              <Controller
                name="parent_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Parent Site"
                    options={parentOptions}
                    errorMsg={errors['parent_id']?.message}
                  />
                )}
              />
            </Box>
          </Box>
        </Form>
      </Modal>
    </FormEditableContext.Provider>,
    (company?: Company) => {
      toggleModal()
      setCompany(company)
    },
  ]
}

export default useAddEditCompanyModal
